<template>
    <div class="d-block mt-5">
        <v-row class="mt-4" v-if="!disabled">
            <v-btn color="primary" small tile @click="addRow" class="ms-3">
                {{ $t('InsertQuestion') }}
                <v-icon class="ms-2">mdi-plus-box</v-icon>
            </v-btn>
            <v-btn color="primary" class="ms-3" small tile @click="add" v-if="questions">
                {{ $t('InsertColumn') }}
                <v-icon class="ms-2">mdi-table-column-plus-after</v-icon>
            </v-btn>
        </v-row>
        <v-row>
            <v-col class="col-12">
                <v-simple-table dense class="bipolar-table">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-start question-col">{{ $t('Question') }}</th>
                                <th v-for="(head, headerIndex) in headers" :key="headerIndex" class="text-center option-col">
                                    <div class="head-cell">
                                        <span class="head-label">{{head.name}}</span>
                                    </div>
                                    <div v-if="!disabled" class="cell-actions">
                                        <v-btn icon x-small :title="$t('Edit')" @click="editColumn(headerIndex)">
                                            <v-icon x-small color="primary">mdi-pencil-outline</v-icon>
                                        </v-btn>
                                        <v-btn icon x-small :title="$t('Delete')" @click="removeColumn(headerIndex)">
                                            <v-icon x-small color="error">mdi-delete-outline</v-icon>
                                        </v-btn>
                                    </div>
                                </th>
                                <th class="actions-col"></th>
                            </tr>
                        </thead>
                        <!-- whole row is draggable (buttons filtered out); radios are disabled
                             placeholders so grabbing them is harmless -->
                        <Draggable tag="tbody" :list="questions" :options="{ animation: 150, filter: 'button', preventOnFilter: false, disabled: disabled }" @end="onRowDragEnd">
                            <tr v-for="(item,rowIndex) in questions" :key="rowIndex">
                                <td class="question-cell" :class="{ 'drag-cursor': !disabled }">
                                    <v-icon v-if="!disabled" small class="drag-cursor handle-icon me-1">mdi-drag</v-icon>
                                    <span>{{item.questionAr}}</span>
                                </td>
                                <td v-for="(column, columnIndex) in headers" :key="columnIndex">
                                    <v-radio disabled class="d-block text-center ma-0"></v-radio>
                                </td>
                                <td class="row-actions">
                                    <v-btn v-if="!disabled" icon small :title="$t('Edit')" @click="editRow(rowIndex)">
                                        <v-icon small color="primary">mdi-pencil-outline</v-icon>
                                    </v-btn>
                                    <v-btn v-if="!disabled" icon small :title="$t('Delete')" @click="removeRow(rowIndex)">
                                        <v-icon small color="error">mdi-delete-outline</v-icon>
                                    </v-btn>
                                </td>

                            </tr>
                        </Draggable>
                    </template>

                </v-simple-table>
            </v-col>
        </v-row>
        <v-row>

            <CommonDialog v-model="dialog" v-if="dialog" :fullscreen="false" width="25%" :title="columnDialogTitle" show-action-toolbar>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="columnName" :label="$t('Answer')" />
                        </v-col>
                    </v-row>
                </v-container>

                <template v-slot:actions>
                    <v-btn color="primary" class="white--text" tile v-if="editColumnFlag === 0" small @click="addColumn">
                        {{ $t("Add") }}
                    </v-btn>
                    <v-btn color="primary" class="white--text" tile v-if="editColumnFlag === 1" small @click="addColumn">
                        {{ $t("Edit") }}
                    </v-btn>
                    <v-btn color="error" class="white--text" tile small @click="dialog = false">
                        {{ $t("Cancel") }}
                    </v-btn>
                </template>
            </CommonDialog>
        </v-row>
        <v-row>
            <CommonDialog v-model="questionDialog" v-if="questionDialog" :fullscreen="false" width="25%" :title="questionDialogTitle" show-action-toolbar>
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="questionName" :label="$t('Question')" />
                        </v-col>
                    </v-row>
                </v-container>

                <template v-slot:actions>
                    <v-btn color="primary" class="white--text" tile v-if="editRowFlag === 0" small @click="addNewQuestion">
                        {{ $t("Add") }}
                    </v-btn>
                    <v-btn color="primary" class="white--text" tile v-if="editRowFlag === 1" small @click="addNewQuestion">
                        {{ $t("Edit") }}
                    </v-btn>
                    <v-btn color="error" class="white--text" tile small @click="questionDialog = false">
                        {{ $t("Cancel") }}
                    </v-btn>
                </template>
            </CommonDialog>
        </v-row>
    </div>

</template>

<script>
    import VTextField from '@/components/controls/VTextField.vue'
    import CommonDialog from '@/components/controls/CommonDialog'
    import Draggable from 'vuedraggable'

    export default {
        components: {
            VTextField, CommonDialog, Draggable
        },

        props: {

            value: {
                type: Array,
                default: () => []
            },
            disabled: {
                type: Boolean,
                default: false
            },
            label: {
                type: String,
                default: function () {
                    return this.$t('BipolarMatrix')
                }
            },
        },

        data() {
            return {
                dialog: false,
                questionDialog: false,
                questionDialogTitle: this.$t('AddQuestion'),
                columnDialogTitle: this.$t('AddAnswer'),
                index: 0,
                nameRules: [(v) => !!v || this.$t('RequiredField')],
                loading: false,
                columnName: '',
                questionName: '',
                // Option columns only — the static "Question" label column is rendered
                // separately in the template. headers[i] always corresponds to
                // questions[*].options[i].
                headers: [],
                questions: this.value,
                editRowFlag: 0,
                selectedRowIndex: 0,
                editColumnFlag: 0,
                selectedColumnIndex: 0

            }
        },

        mounted() {
            if (this.questions) {
                // defensive: display in persisted order even if the API returned unsorted arrays
                this.questions.sort((a, b) => (a.orderBy != null ? a.orderBy : Infinity) - (b.orderBy != null ? b.orderBy : Infinity))
                this.questions.forEach(q => {
                    if (q.options) q.options.sort((a, b) => (a.order != null ? a.order : Infinity) - (b.order != null ? b.order : Infinity))
                })
                if (this.questions.length > 0) {
                    for (var j = 0; j < this.questions[0].options.length; j++) {
                        this.headers.push({ name: this.questions[0].options[j].optionAr, removable: true })
                    }
                }
                this.index = this.headers.length
            }
        },

        methods: {
            emitChange() {
                // Keep persisted ordering fields in sync with array order: the public
                // form sorts options by `order`, and sub-questions save `orderBy`.
                this.questions.forEach((q, i) => {
                    q.orderBy = i + 1
                    if (q.options) q.options.forEach((o, j) => { o.order = j + 1 })
                })
                this.$emit('input', this.questions)
            },

            onRowDragEnd() {
                this.emitChange()
            },

            add() {
                this.dialog = true
            },
            editColumn(index) {
                this.columnDialogTitle = this.$t('Edit')
                this.dialog = true
                var selectedColumn = this.headers[index]
                if (selectedColumn) {
                    this.columnName = selectedColumn.name
                    this.editColumnFlag = 1
                    this.selectedColumnIndex = index
                }
            },
            addColumn() {
                if (this.editColumnFlag == 1) {
                    this.headers[this.selectedColumnIndex].name = this.columnName
                    for (var i = 0; i < this.questions.length; i++) {
                        this.questions[i].options[this.selectedColumnIndex].optionAr = this.columnName
                        this.questions[i].options[this.selectedColumnIndex].optionEn = this.columnName
                    }
                    this.editColumnFlag = 0
                    this.selectedColumnIndex = 0
                } else {
                    if (this.columnName) {
                        this.headers.push({ name: this.columnName, removable: true })
                        for (var i = 0; i < this.questions.length; i++) {
                            // fresh object per row — a shared reference would make
                            // editing one row's option rewrite every row
                            this.questions[i].options.push({ optionAr: this.columnName, optionEn: this.columnName })
                        }
                    }
                    this.index++
                }
                this.columnName = ''
                this.dialog = false
                this.emitChange()

            },
            remove() {
                this.headers.pop()
                this.questions.forEach(item => { item.options.pop() })
                this.index--
                this.emitChange()
            },
            removeColumn(index) {
                this.headers.splice(index, 1)
                for (var i = 0; i < this.questions.length; i++) {
                    this.questions[i].options.splice(index, 1)
                }
                this.index--
                this.emitChange()
            },
            addRow() {
                this.questionDialog = true
                this.questionDialogTitle = this.$t('AddQuestion')

            },
            addNewQuestion() {

                if (this.editRowFlag == 1) {
                    this.questions[this.selectedRowIndex].questionAr = this.questionName
                    this.questions[this.selectedRowIndex].questionEn = this.questionName
                    this.editRowFlag = 0
                    this.selectedRowIndex = 0
                } else {
                    if (this.questionName) {
                        this.questions.push({ questionAr: this.questionName, questionEn: this.questionName, options: [] })
                        this.addOptions()
                    }
                }
                this.questionName = ''
                this.questionDialog = false
                this.emitChange()

            },
            removeRow(index) {
                this.questions.splice(index, 1)
                this.emitChange()
            },
            editRow(index) {
                this.questionDialogTitle = this.$t('Edit')
                this.questionDialog = true
                var selectedQuestion = this.questions[index]
                if (selectedQuestion) {
                    this.questionName = selectedQuestion.questionAr
                    this.editRowFlag = 1
                    this.selectedRowIndex = index
                }

            },
            addOptions() {
                let firstElement = this.questions[0]
                let lastElement = this.questions.slice(-1)[0]
                for (var i = 0; i < firstElement.options.length; i++) {
                    // copy — pushing the same object would share it between rows
                    lastElement.options.push({ ...firstElement.options[i] })
                }
            }
        }
    }
</script>

<style scoped>
    .bipolar-table {
        border: 1px solid #dde4e1;
        border-radius: 8px;
        overflow: hidden;
    }

    .bipolar-table >>> thead th {
        background-color: #eef5f2;
        border-bottom: 2px solid #cfe0d9 !important;
        padding: 8px 6px !important;
        vertical-align: top;
        white-space: nowrap;
    }

    .bipolar-table >>> thead th.question-col {
        font-size: 0.85rem;
        vertical-align: middle;
    }

    .head-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2px;
    }

    .head-label {
        font-size: 0.8rem;
        font-weight: 600;
    }

    .cell-actions {
        display: flex;
        justify-content: center;
        gap: 2px;
        margin-top: 2px;
        opacity: 0.45;
        transition: opacity 0.15s ease;
    }

    .bipolar-table >>> thead th:hover .cell-actions { opacity: 1; }

    .bipolar-table >>> tbody td {
        border-bottom: 1px solid #eef1f0 !important;
        padding: 4px 6px !important;
    }

    /* whole rows are drag targets */
    .bipolar-table >>> tbody tr { user-select: none; }

    .question-cell {
        font-size: 0.85rem;
        min-width: 160px;
    }

    .row-actions {
        white-space: nowrap;
        text-align: center;
        opacity: 0.45;
        transition: opacity 0.15s ease;
    }

    .bipolar-table >>> tbody tr:hover .row-actions { opacity: 1; }

    tbody tr:nth-of-type(2n) { background-color: #f6f8f7 !important; }
    .bipolar-table >>> tbody tr:hover { background-color: #eef5f2 !important; }

    .drag-cursor { cursor: move; user-select: none; }
    .handle-icon { color: #9bb3aa !important; }
    .handle-icon:hover { color: #1b6e53 !important; }
    .sortable-ghost { opacity: 0.4; background-color: #d3e8de !important; }
    .sortable-chosen { background-color: #e4f0eb !important; }
</style>